import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Trash2, X, Pencil } from "lucide-react";

export default function Fleet() {
  const [trucks, setTrucks] = useState([]);
  const [haulers, setHaulers] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    const [t, h] = await Promise.all([api.get("/trucks"), api.get("/haulers")]);
    setTrucks(t.data); setHaulers(h.data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!window.confirm("Remove this truck?")) return;
    await api.delete(`/trucks/${id}`);
    refresh();
  };
  const haulerName = (id) => haulers.find((h) => h.id === id)?.company_name || "—";

  return (
    <div>
      <PageHeader
        title="Fleet"
        subtitle="Trucks and equipment"
        actions={
          <button onClick={() => setEditing("new")} data-testid="new-truck-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
            + New Truck
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="trucks-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 label-caps">Truck #</th>
                <th className="text-left px-4 py-2.5 label-caps">Plate</th>
                <th className="text-right px-4 py-2.5 label-caps">Capacity</th>
                <th className="text-right px-4 py-2.5 label-caps">Tare</th>
                <th className="text-left px-4 py-2.5 label-caps">Hauler</th>
                <th className="text-left px-4 py-2.5 label-caps">DOT #</th>
                <th className="text-left px-4 py-2.5 label-caps">Status</th>
                <th className="text-right px-4 py-2.5 label-caps">Actions</th>
              </tr>
            </thead>
            <tbody>
              {trucks.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-gray-500">No trucks yet.</td></tr>
              ) : trucks.map((t) => (
                <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm text-gray-900">{t.truck_number}</td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-700">{t.plate}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm">{t.capacity_tons} t</td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-gray-600">{t.tare_weight_tons || "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 truncate max-w-[160px]">{haulerName(t.hauler_id)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{t.dot_number || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-gray-700">{(t.status || "").replace("_", " ")}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing(t)} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100" data-testid={`edit-truck-${t.truck_number}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(t.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-truck-${t.truck_number}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <TruckDialog
          initial={editing === "new" ? null : editing}
          haulers={haulers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function TruckDialog({ initial, haulers, onClose, onSaved }) {
  const [form, setForm] = useState({
    truck_number: initial?.truck_number || "",
    plate: initial?.plate || "",
    capacity_tons: initial?.capacity_tons ?? 20,
    tare_weight_tons: initial?.tare_weight_tons ?? 0,
    dot_number: initial?.dot_number || "",
    status: initial?.status || "available",
    hauler_id: initial?.hauler_id || "",
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, hauler_id: form.hauler_id || null };
      if (isEdit) await api.patch(`/trucks/${initial.id}`, payload);
      else await api.post("/trucks", payload);
      toast.success(isEdit ? "Truck updated" : "Truck added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-xl">{isEdit ? "Edit Truck" : "New Truck"}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Truck #"><input required value={form.truck_number} onChange={(e) => set("truck_number", e.target.value)} className="inp" data-testid="truck-number-input" /></F>
          <F label="Plate"><input required value={form.plate} onChange={(e) => set("plate", e.target.value)} className="inp" data-testid="truck-plate-input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Capacity (t)"><input type="number" step="0.1" required value={form.capacity_tons} onChange={(e) => set("capacity_tons", parseFloat(e.target.value))} className="inp" data-testid="truck-capacity-input" /></F>
            <F label="Tare Weight (t)"><input type="number" step="0.1" value={form.tare_weight_tons} onChange={(e) => set("tare_weight_tons", parseFloat(e.target.value) || 0)} className="inp" data-testid="truck-tare-input" /></F>
          </div>
          <F label="Hauler">
            <select value={form.hauler_id} onChange={(e) => set("hauler_id", e.target.value)} className="inp" data-testid="truck-hauler-select">
              <option value="">Unassigned</option>
              {haulers.map((h) => <option key={h.id} value={h.id}>{h.company_name}</option>)}
            </select>
          </F>
          <F label="DOT #"><input value={form.dot_number} onChange={(e) => set("dot_number", e.target.value)} className="inp font-mono" data-testid="truck-dot-input" /></F>
          <F label="Status">
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="inp" data-testid="truck-status-select">
              <option value="available">Available</option>
              <option value="in_use">In Use</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </F>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-truck-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold">{isEdit ? "Update" : "Save"}</button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>;
}
